import React from 'react';
import Modal from 'react-bootstrap/Modal';
import { OrderForListing } from '../../models/OrderForListing';
import { usePrintListOrders } from '../../hooks/usePrintListOrders';
import ServicesContext from '../../contexts/ServicesContext';
import { useAuth } from '../../contexts/AuthContext';
import { useDispatch } from 'react-redux';
import { MainActions } from '../../actions/MainActions';
import { SeparationType } from '../../enums/SeparationType';
import { FileFormat } from '../../enums/FileFormat';
import { CallbackResultType } from '../../enums/CallbackResultType';
import { TSOBarcodeMode } from '../../enums/TSOBarcodeMode';
import { DanfeLabelBarcodeMode } from '../../enums/DanfeLabelBarcodeMode';

enum ReportType {
  ShippingGuide = 'shipping_guide',
  SeparationList = 'separation_list',
  LensesSeparationListPdf = 'lenses_separation_list_pdf',
  LensesSeparationListCsv = 'lenses_separation_list_csv',
  TSO = 'tso',
  DanfePackingLabelZpl = 'danfe_packing_label_zpl',
  DanfePackingLabelZipZpl = 'danfe_packing_label_zip_zpl',
  DanfePackingLabelPdf = 'danfe_packing_label_pdf',
  SimplePackingLabelZpl = 'simple_packing_label_zpl',
  SimplePackingLabelPdf = 'simple_packing_label_pdf',
  LensCertificateLabelZpl = 'lens_certificate_label_zpl',
  LensCertificateLabelPdf = 'lens_certificate_label_pdf',
}
const reportTypeMap = {
  [ReportType.ShippingGuide]: (version: number | undefined) => version ? `Guia de Remessa (v${version})` : 'Guia de Remessa',
  [ReportType.SeparationList]: (version: number | undefined) => 'Lista de Separação',
  [ReportType.LensesSeparationListPdf]: (version: number | undefined) => 'Lista de Separação de Lentes em PDF',
  [ReportType.LensesSeparationListCsv]: (version: number | undefined) => 'Lista de Separação de Lentes em CSV',
  [ReportType.TSO]: (version: number | undefined) => version == 2 ? 'TSO A5 (v2)' : 'TSO A4 (v1)',
  [ReportType.DanfePackingLabelZpl]: (version: number | undefined) => 'Etiquetas de Empacotamento Danfe em ZPL',
  [ReportType.DanfePackingLabelZipZpl]: (version: number | undefined) => 'Etiquetas de Empacotamento Danfe em ZPL (ZIP)',
  [ReportType.DanfePackingLabelPdf]: (version: number | undefined) => 'Etiquetas de Empacotamento Danfe em PDF',
  [ReportType.SimplePackingLabelZpl]: (version: number | undefined) => 'Etiquetas de Empacotamento Simples em ZPL',
  [ReportType.SimplePackingLabelPdf]: (version: number | undefined) => 'Etiquetas de Empacotamento Simples em PDF',
  [ReportType.LensCertificateLabelZpl]: (version: number | undefined) => 'Etiquetas de Certificado de Lentes em ZPL',
  [ReportType.LensCertificateLabelPdf]: (version: number | undefined) => 'Etiquetas de Certificado de Lentes em PDF',
}

interface ILongRunningReportProgressModalProps {
  reportType?: ReportType;
  reportVersion?: number;
  orderForListing?: OrderForListing[];
  queueJob?: boolean;
  separationType?: SeparationType | null;
  fileFormat?: FileFormat | null;
  barcodeMode?: TSOBarcodeMode;
  danfeLabelBarcodeMode?: DanfeLabelBarcodeMode;
  close: () => void;
}

const waitingJobTickInterval = 2000;
const waitingJobTickMax = 10;

const LongRunningReportProgressModal: React.FC<ILongRunningReportProgressModalProps> = ({
  reportType,
  orderForListing,
  reportVersion,
  queueJob,
  separationType,
  fileFormat,
  barcodeMode,
  danfeLabelBarcodeMode,
  close
}) => {

  const dispatch = useDispatch();
  const mainActions = React.useMemo(() => new MainActions(dispatch), [dispatch]);

  const { session } = useAuth();
  const [generating, setGenerating] = React.useState(false);
  const [waitingData, setWaitingData] = React.useState<number>(5);
  const [reportJobQueueId, setReportJobQueueId] = React.useState<number>(0);
  const [waitingJobTick, setWaitingJobTick] = React.useState<number>(waitingJobTickMax);
  const [message, setMessage] = React.useState<string>('');

  const waitingJob = React.useRef(false);

  const {
    getReportJobQueueByIdService
  } = React.useContext(ServicesContext);

  const {
    printShippingGuide,
    queuePrintShippingGuide,
    printListToSeparate,
    printLensSeparationList,
    printTSO,
    printPdfDanfePackingLabel,
    printZplDanfePackingLabel,
    printZipZplDanfePackingLabel,
    printZplSimplePackingLabel,
    printPdfSimplePackingLabel,
    printZplLensCertificateLabel,
    printPdfLensCertificateLabel
  } = usePrintListOrders();

  React.useEffect(() => {
    setTimeout(() => {
      setGenerating(true);
    }, 1000);
  }, [])

  React.useEffect(() => {
    if (!generating) return;
    const count = orderForListing?.length || 0;
    if (count === 0) {
      if (waitingData > 0) {
        setTimeout(() => {
          setWaitingData(waitingData - 1);
        }, 1000);
        return;
      } else {
        setGenerating(false);
        setTimeout(() => {
          close();
        }, 1000);
      }
    }
    if (reportType === ReportType.ShippingGuide) {
      if (queueJob) {
        mainActions.setNotWait(true);
        queuePrintShippingGuide(orderForListing || [], session?.user?.id || 0, reportVersion || 0)
          .then((result) => {
            const rptJobQueueId = result?.value?.reportJobQueueId || 0;
            if (rptJobQueueId <= 0) {
              setMessage(result?.value?.message || 'Não foi possível gerar a guia de remessa.');
              setGenerating(false);
              return;
            }
            waitingJob.current = true;
            setReportJobQueueId(rptJobQueueId);
            setWaitingJobTick(waitingJobTickMax);
          })
      } else {
        mainActions.setNotWait(true);
        printShippingGuide(orderForListing || [], reportVersion || 0, (success: boolean) => {
          mainActions.setNotWait(false);
          if (success) {
            setGenerating(false);
            setTimeout(() => {
              close();
            }, 1000);
          }
        });
      }
    }
    if (reportType === ReportType.SeparationList) {
      mainActions.setNotWait(true);
      printListToSeparate(separationType!, orderForListing || [], (success: boolean) => {
        mainActions.setNotWait(false);
        if (success) {
          setGenerating(false);
          setTimeout(() => {
            close();
          }, 1000);
        }
      });
    }
    if (reportType === ReportType.LensesSeparationListPdf || reportType === ReportType.LensesSeparationListCsv) {
      mainActions.setNotWait(true);
      printLensSeparationList(fileFormat!, orderForListing || [], (resultType: CallbackResultType) => {
        mainActions.setNotWait(false);
        if (resultType === CallbackResultType.Success || resultType === CallbackResultType.Cancelled) {
          setGenerating(false);
          setTimeout(() => {
            close();
          }, 1000);
        }
      });
    }
    if (reportType === ReportType.TSO) {
      mainActions.setNotWait(true);
      printTSO(orderForListing || [], reportVersion || 0, barcodeMode, (resultType: CallbackResultType) => {
        mainActions.setNotWait(false);
        if (resultType === CallbackResultType.Success || resultType === CallbackResultType.Cancelled) {
          setGenerating(false);
          setTimeout(() => {
            close();
          }, 1000);
        }
      });
    }
    if (reportType === ReportType.DanfePackingLabelPdf) {
      mainActions.setNotWait(true);
      printPdfDanfePackingLabel(orderForListing || [], (resultType: CallbackResultType) => {
        mainActions.setNotWait(false);
        if (resultType === CallbackResultType.Success || resultType === CallbackResultType.Cancelled) {
          setGenerating(false);
          setTimeout(() => {
            close();
          }, 1000);
        }
      });
    }
    if (reportType === ReportType.DanfePackingLabelZpl) {
      mainActions.setNotWait(true);
      printZplDanfePackingLabel(orderForListing || [], (resultType: CallbackResultType) => {
        mainActions.setNotWait(false);
        if (resultType === CallbackResultType.Success || resultType === CallbackResultType.Cancelled) {
          setGenerating(false);
          setTimeout(() => {
            close();
          }, 1000);
        }
      }, danfeLabelBarcodeMode);
    }
    if (reportType === ReportType.DanfePackingLabelZipZpl) {
      mainActions.setNotWait(true);
      printZipZplDanfePackingLabel(orderForListing || [], (resultType: CallbackResultType) => {
        mainActions.setNotWait(false);
        if (resultType === CallbackResultType.Success || resultType === CallbackResultType.Cancelled) {
          setGenerating(false);
          setTimeout(() => {
            close();
          }, 1000);
        }
      }, danfeLabelBarcodeMode);
    }
    if (reportType === ReportType.SimplePackingLabelZpl) {
      mainActions.setNotWait(true);
      printZplSimplePackingLabel(orderForListing || [], (resultType: CallbackResultType) => {
        mainActions.setNotWait(false);
        if (resultType === CallbackResultType.Success || resultType === CallbackResultType.Cancelled) {
          setGenerating(false);
          setTimeout(() => {
            close();
          }, 1000);
        }
      });
    }
    if (reportType === ReportType.SimplePackingLabelPdf) {
      mainActions.setNotWait(true);
      printPdfSimplePackingLabel(orderForListing || [], (resultType: CallbackResultType) => {
        mainActions.setNotWait(false);
        if (resultType === CallbackResultType.Success || resultType === CallbackResultType.Cancelled) {
          setGenerating(false);
          setTimeout(() => {
            close();
          }, 1000);
        }
      });
    }
    if (reportType === ReportType.LensCertificateLabelPdf) {
      mainActions.setNotWait(true);
      printPdfLensCertificateLabel(orderForListing || [], (resultType: CallbackResultType) => {
        mainActions.setNotWait(false);
        if (resultType === CallbackResultType.Success || resultType === CallbackResultType.Cancelled) {
          setGenerating(false);
          setTimeout(() => {
            close();
          }, 1000);
        }
      });
    }
    if (reportType === ReportType.LensCertificateLabelZpl) {
      mainActions.setNotWait(true);
      printZplLensCertificateLabel(orderForListing || [], (resultType: CallbackResultType) => {
        mainActions.setNotWait(false);
        if (resultType === CallbackResultType.Success || resultType === CallbackResultType.Cancelled) {
          setGenerating(false);
          setTimeout(() => {
            close();
          }, 1000);
        }
      });
    }
  }, [generating, waitingData]);

  React.useEffect(() => {
    if (!waitingJob.current || reportJobQueueId <= 0) return;

    if (waitingJobTick < 0) {
      waitingJob.current = false;
      mainActions.setNotWait(false);
      setMessage('A geração está demorando mais do que esperado. Acesse a lista de relatórios.');
      setWaitingJobTick(waitingJobTickMax);
      return;
    }

    getReportJobQueueByIdService.handle(reportJobQueueId)
      .then((result) => {
        const fileUrl = result?.value?.reportJobQueue?.result || '';
        if (fileUrl) {
          waitingJob.current = false;
          setWaitingJobTick(waitingJobTickMax);
          close();
          return window.open(fileUrl, '_blank');
        }
        setTimeout(() => {
          setWaitingJobTick(waitingJobTick - 1);
        }, waitingJobTickInterval);
      })
      .catch(() => {
        setWaitingJobTick(-1);
      });
  }, [reportJobQueueId, waitingJob.current, waitingJobTick]);

  return (
    <Modal
      show={true}
      onHide={() => clickCloseModal()}
      backdrop="static"
      keyboard={false}
      dialogClassName='modal-600px'
    >
      <Modal.Header closeButton style={{ height: '50px' }}>
        <Modal.Title>
          Progresso de Geração
        </Modal.Title>
      </Modal.Header>

      <Modal.Body id='longRunningReportProgressModalBody' style={{ maxHeight: '85vh', overflowY: 'auto' }}>
        <div>
          <span>{message ? message : 'Aguarde, gerando'}</span>
          {message ? null : (
            <>
              <span style={{ marginLeft: '5px', fontWeight: 'bold' }}>
                {reportType ? reportTypeMap[reportType]?.(reportVersion) || '' : ''}
                {reportType === ReportType.TSO && barcodeMode ? ' - teste' : ''}
                {danfeLabelBarcodeMode ? ' - teste' : ''}
              </span>
              <span>...</span>
            </>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );

  function clickCloseModal() {
    close();
  }
};

export { LongRunningReportProgressModal, ReportType };